import {
  FloatingNode,
  FloatingPortal,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'

import { useLockBodyScroll } from '@/hooks/use-lock-body-scroll'

import * as S from './overlay.styled'
import { OverlayProps } from './overlay.types'
import { useOverlayControl } from './overlay-control.context'

export const Overlay = ({
  children,
  onClose,
  closeable = true,
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  onBeforeClose,
  role: roleType = 'dialog',
  lockScroll = true,
  ...props
}: OverlayProps) => {
  // NOTE: When used inside popupFactory, isClosing/onAfterClose are injected via context.
  // isClosing signals the overlay to start its close animation.
  // onAfterClose is called once the animation completes (actual unmount trigger).
  useLockBodyScroll(lockScroll)

  const {
    isClosing: ctxIsClosing,
    onAfterClose,
    registerAnimated,
    isSuspended,
    hasSuspendedBelow,
  } = useOverlayControl()

  // NOTE: By default, `Overlay` is rendered in the 'closed' state and is being in transition to 'opened' state
  const [isInTransition, setIsInTransition] = useState(true)
  // NOTE: isOpening controls the backdrop opacity.
  // When opening on top of a suspended modal, start at 1 immediately so the backdrop never flashes out.
  const [isOpening, setIsOpening] = useState(hasSuspendedBelow)
  // NOTE: isContentOpening always starts false so the dialog box can animate in independently.
  // For stacked modals the backdrop is already solid, so only the content needs to transition.
  const [isContentOpening, setIsContentOpening] = useState(false)
  const [overlayElement, setOverlayElement] = useState<HTMLDivElement | null>(null)
  const nodeId = useFloatingNodeId()

  const handleClose = useCallback(() => {
    // NOTE: A suspended popup is hidden under the active one, so outside-press / Escape
    // must not start its close transition (it would never finish while display: none).
    if (!closeable || isSuspended) return
    const closePermission = onBeforeClose?.()

    if (closePermission instanceof Promise) {
      void closePermission.then((shouldClose) => {
        if (shouldClose === false) return
        // NOTE: For stacked modals, skip the backdrop fade-out to avoid the suspended
        // modal's overlay flashing into view. Pop the stack directly instead.
        if (hasSuspendedBelow) {
          onAfterClose?.()
          return
        }
        setIsOpening(false)
      })
      return
    }

    if (closePermission === false) return

    if (hasSuspendedBelow) {
      onAfterClose?.()
      return
    }
    setIsOpening(false)
  }, [closeable, isSuspended, onBeforeClose, hasSuspendedBelow, onAfterClose])

  const handleTransitionStart = useCallback((e: React.TransitionEvent<HTMLDivElement>) => {
    // NOTE: transitions bubbles up, so check that transitions are fired on the correct target
    if (e.target !== e.currentTarget) return

    setIsInTransition(true)
  }, [])

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      // NOTE: transitions bubbles up, so check that transitions are fired on the correct target
      if (e.target !== e.currentTarget) return

      setIsInTransition(false)
      // NOTE: onAfterClose is provided by popupFactory and handles actual unmounting after animation.
      // For direct (non-popupFactory) usage, fall back to onClose as before.
      if (!isOpening) (onAfterClose ?? onClose)()
    },
    [isOpening, onClose, onAfterClose],
  )

  const { refs, context } = useFloating({
    nodeId,
    onOpenChange: (open) => {
      if (!open) handleClose()
    },
    open: true,
  })

  const role = useRole(context, { role: roleType })
  const dismiss = useDismiss(context, {
    // Note: Stacked popups render as React siblings (see PopupProvider), not nested inside
    // each other, so floating-ui's FloatingTree has no parent/child link between them. Without
    // this guard, a suspended (hidden) overlay still has its own outside-press/escape-key
    // listeners active, so interacting with the popup stacked on top of it (e.g. clicking
    // Confirm in a nested ConfirmationPopup) is seen as an "outside" interaction here too -
    // it silently fades this overlay to opacity:0 before it's ever restored to active.
    enabled: closeable && !isSuspended,
    escapeKey: closeOnEscapeKey,
    outsidePress: closeOnOverlayClick,
    outsidePressEvent: 'mousedown',
  })

  const { getFloatingProps } = useInteractions([role, dismiss])

  useLayoutEffect(() => {
    registerAnimated?.()
  }, [])

  // NOTE: React to an external close signal from popupFactory (via context)
  useEffect(() => {
    if (!ctxIsClosing) return

    // Note: External close requests (e.g. popupFactory close) should bypass onBeforeClose.
    // onBeforeClose is intended only for user-triggered dismiss actions.
    // For stacked modals, skip the backdrop fade to avoid flashing the suspended overlay below.
    if (hasSuspendedBelow) {
      onAfterClose?.()
      return
    }
    setIsOpening(false)
  }, [ctxIsClosing, hasSuspendedBelow, onAfterClose])

  // NOTE: this logic will gracefully handle the initial opening of the overlay, cause
  // `@floating-ui/react` has its own internal logic that forces children to be rendered
  // slightly differently in React
  useEffect(() => {
    if (!overlayElement) return
    setIsOpening(true)
    setIsContentOpening(true)
  }, [overlayElement])

  return (
    <FloatingNode id={nodeId}>
      <FloatingPortal>
        <S.Overlay
          {...props}
          ref={setOverlayElement}
          lockScroll={false}
          $isOpening={isOpening}
          $isSuspended={isSuspended}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        >
          <S.ContentWrapper
            $isContentOpening={isContentOpening}
            $hasSuspendedBelow={hasSuspendedBelow}
          >
            {children({
              getFloatingProps,
              handleClose,
              isInTransition,
              isOpening,
              setFloating: refs.setFloating,
            })}
          </S.ContentWrapper>
        </S.Overlay>
      </FloatingPortal>
    </FloatingNode>
  )
}
